import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import SearchInput from "@/components/SearchInput";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { can } from "@/lib/permissions";
import { toast } from "sonner";
import { Plus, Users, Edit, CheckCircle, XCircle, Loader2, Eye, EyeOff, Building2, ShieldCheck, ShieldX, Truck, GripVertical, ArrowUp, ArrowDown, Trash2, Settings, Clock, Mail, PackageCheck, Layers, AlertTriangle, ScrollText } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Pagination } from "@/components/ui-system";
import SellerRateCardManager from "@/components/SellerRateCardManager";

const PAGE_SIZE = 50;

// Format date to IST
const formatToIST = (dateString) => {
  if (!dateString) return 'N/A';
  // Ensure the date string is treated as UTC by appending 'Z' if not present
  let utcDateString = dateString;
  if (typeof dateString === 'string' && !dateString.endsWith('Z') && !dateString.includes('+')) {
    utcDateString = dateString + 'Z';
  }
  const date = new Date(utcDateString);
  return date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Reusable "Shipping Partner Priority (order of preference)" editor — a reorderable
 * list of shipping partners with an add-dropdown. Used by the bulk LRN-assignment
 * dialog (and mirrors the per-seller settings block).
 */
function PartnerPriorityEditor({ priority, setPriority, partners }) {
  const getName = (id) => partners.find((p) => p.id === id)?.name || id;
  const add = (id) => { if (id && !priority.includes(id)) setPriority([...priority, id]); };
  const remove = (id) => setPriority(priority.filter((p) => p !== id));
  const swap = (i, j) => {
    if (j < 0 || j >= priority.length) return;
    const next = [...priority];
    [next[i], next[j]] = [next[j], next[i]];
    setPriority(next);
  };
  const available = partners.filter((p) => !priority.includes(p.id));
  return (
    <div className="space-y-3">
      <Label>Shipping Partner Priority (order of preference)</Label>
      <p className="text-xs text-slate-500">
        Applied to all selected sellers. Auto assignment tries partners in this order; if one fails, it tries the next.
      </p>
      {priority.length > 0 && (
        <div className="space-y-2 bg-slate-50 p-3 rounded-lg">
          {priority.map((partnerId, index) => (
            <div key={partnerId} className="flex items-center gap-2 bg-white p-2 rounded border">
              <span className="text-sm font-semibold text-slate-600 w-6">{index + 1}.</span>
              <GripVertical className="w-4 h-4 text-slate-400" />
              <span className="flex-1 text-sm font-medium">{getName(partnerId)}</span>
              <div className="flex items-center gap-1">
                <Button type="button" size="sm" variant="ghost" onClick={() => swap(index, index - 1)} disabled={index === 0} className="h-7 w-7 p-0">
                  <ArrowUp className="w-3 h-3" />
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => swap(index, index + 1)} disabled={index === priority.length - 1} className="h-7 w-7 p-0">
                  <ArrowDown className="w-3 h-3" />
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => remove(partnerId)} className="h-7 w-7 p-0 text-red-500 hover:text-red-700">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {available.length > 0 && (
        <Select onValueChange={add}>
          <SelectTrigger className="flex-1">
            <SelectValue placeholder="Add shipping partner to priority list" />
          </SelectTrigger>
          <SelectContent>
            {available.map((partner) => (
              <SelectItem key={partner.id} value={partner.id}>
                {partner.name} {!partner.is_active && "(Inactive)"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {priority.length === 0 && (
        <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded">
          ⚠️ Add at least one shipping partner to enable auto assignment.
        </p>
      )}
    </div>
  );
}

const SellersManagement = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [rateCardSeller, setRateCardSeller] = useState(null); // seller whose B2B rate-card manager is open
  const [dirty, setDirty] = useState(false); // any unsaved edit in the edit dialog
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [editSeller, setEditSeller] = useState(null);
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  // Filters (client-side, layered on the server-side search) + client-side pagination
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | inactive
  const [lrnFilter, setLrnFilter] = useState("all"); // all | auto | manual
  const [page, setPage] = useState(1);
  // Bulk LRN assignment
  const [selectedSellerIds, setSelectedSellerIds] = useState(() => new Set());
  const [showBulkLrnDialog, setShowBulkLrnDialog] = useState(false);
  const [bulkAssignmentRule, setBulkAssignmentRule] = useState("auto");
  // Partner priority applied to ALL selected sellers when the bulk rule is "auto".
  const [bulkPartnerPriority, setBulkPartnerPriority] = useState([]);
  const [applyingBulk, setApplyingBulk] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Shipping Assignment State
  const [shippingPartners, setShippingPartners] = useState([]);
  const [shippingAssignmentRule, setShippingAssignmentRule] = useState("manual");
  const [shippingPartnerPriority, setShippingPartnerPriority] = useState([]);

  // B2C config state (only loaded/saved when the user can manage it)
  const canManageB2C = can(user, "sellers.b2c_config:manage");
  const [b2cConfig, setB2cConfig] = useState(null); // {b2c_enabled, b2c_plan_id, plan_name, b2c_cod_cycle_days, effective_cod_cycle_days}
  const [b2cPlans, setB2cPlans] = useState([]); // active plans for the dropdown
  // B2C shipping partners. A saved `b2c_partner_ids: []` means "every active partner",
  // so in that case the UI shows every active partner ticked.
  const [b2cPartnerSel, setB2cPartnerSel] = useState([]); // ticked ACTIVE partner ids
  const [b2cPartnersTouched, setB2cPartnersTouched] = useState(false); // only send when edited
  const [b2cPartnersOpen, setB2cPartnersOpen] = useState(""); // accordion value

  const b2cPartners = b2cConfig?.available_partners || [];
  const b2cActivePartners = b2cPartners.filter((p) => p.enabled);
  const b2cInactivePartners = b2cPartners.filter((p) => !p.enabled);
  const b2cSavedPartnerIds = b2cConfig?.b2c_partner_ids || [];
  const b2cAllActiveSelected =
    b2cActivePartners.length > 0 && b2cActivePartners.every((p) => b2cPartnerSel.includes(p.id));
  // One-line state shown on the collapsed accordion header.
  const b2cPartnerSummary =
    b2cActivePartners.length === 0
      ? { label: "No active partners", className: "bg-slate-100 text-slate-600" }
      : b2cPartnerSel.length === 0
      ? { label: "No active partner selected", className: "bg-amber-100 text-amber-800" }
      : b2cAllActiveSelected
      ? { label: `All active partners (${b2cActivePartners.length})`, className: "bg-brand-bg text-brand-dark" }
      : {
          label: `${b2cPartnerSel.length} of ${b2cActivePartners.length} active selected`,
          className: "bg-blue-100 text-blue-800",
        };

  const [formData, setFormData] = useState({
    company_registered_name: "",
    brand_name: "",
    mobile_number: "",
    gst_number: "",
    registered_address: "",
    pincode: "",
    city: "",
    state: "",
    admin_email: "",
    admin_password: "",
    admin_name: "",
    admin_phone: "",
    min_wallet_balance: "0",
    enforce_wallet_minimum_for_orders: true,
  });

  // Wallet restriction settings
  const [walletSettings, setWalletSettings] = useState({ approver_emails: [], notification_to_emails: [], notification_cc_emails: [], notification_bcc_emails: [] });
  const [newApproverEmail, setNewApproverEmail] = useState("");
  const [newToEmail, setNewToEmail] = useState("");
  const [newCcEmail, setNewCcEmail] = useState("");
  const [newBccEmail, setNewBccEmail] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);

  // Exemption request
  const [showExemptionDialog, setShowExemptionDialog] = useState(false);
  const [exemptionSeller, setExemptionSeller] = useState(null);
  const [exemptionDays, setExemptionDays] = useState("7");
  const [requestingExemption, setRequestingExemption] = useState(false);
  const [exemptionHistory, setExemptionHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    fetchSellers();
    fetchShippingPartners();
    fetchWalletSettings();
  }, []);

  const fetchSellers = async (query = "") => {
    try {
      const url = query ? `${API}/sellers-org/?q=${encodeURIComponent(query)}` : `${API}/sellers-org/`;
      const response = await axios.get(url);
      setSellers(response.data);
    } catch (error) {
      toast.error("Failed to fetch sellers");
    } finally {
      setLoading(false);
    }
  };

  const fetchShippingPartners = async () => {
    try {
      const response = await axios.get(`${API}/b2b-shipping-partner/`);
      setShippingPartners(response.data);
    } catch (error) {
      console.error("Failed to fetch shipping partners:", error);
    }
  };

  const fetchWalletSettings = async () => {
    try {
      const { data } = await axios.get(`${API}/wallet-restriction/settings`);
      setWalletSettings(data);
    } catch {}
  };

  const saveWalletSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/wallet-restriction/settings`, walletSettings);
      toast.success("Approver settings saved");
      setShowSettingsDialog(false);
    } catch (err) {
      toast.error("Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const addEmail = (field, value, setter) => {
    const email = value.trim().toLowerCase();
    if (!email || !email.includes("@")) return;
    if (walletSettings[field].includes(email)) return;
    setWalletSettings({ ...walletSettings, [field]: [...walletSettings[field], email] });
    setter("");
  };

  const removeEmail = (field, email) => {
    setWalletSettings({ ...walletSettings, [field]: walletSettings[field].filter((e) => e !== email) });
  };

  const handleRequestExemption = async () => {
    if (!exemptionSeller || !exemptionDays) return;
    setRequestingExemption(true);
    try {
      await axios.post(`${API}/wallet-restriction/request-exemption`, {
        seller_id: exemptionSeller.seller_id,
        exemption_days: parseInt(exemptionDays),
      });
      toast.success("Exemption request sent for approval");
      setShowExemptionDialog(false);
      setExemptionDays("7");
      fetchSellers();
      if (exemptionSeller) fetchExemptionHistory(exemptionSeller.seller_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to request exemption");
    } finally {
      setRequestingExemption(false);
    }
  };

  const fetchExemptionHistory = async (sellerId) => {
    setHistoryLoading(true);
    try {
      const { data } = await axios.get(`${API}/wallet-restriction/requests?seller_id=${sellerId}`);
      setExemptionHistory(data);
    } catch {
      setExemptionHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchShippingAssignment = async (sellerId) => {
    try {
      const response = await axios.get(`${API}/sellers-org/${sellerId}/shipping-assignment`);
      setShippingAssignmentRule(response.data.shipping_assignment_rule || "manual");
      setShippingPartnerPriority(response.data.shipping_partner_priority || []);
    } catch (error) {
      console.error("Failed to fetch shipping assignment:", error);
      setShippingAssignmentRule("manual");
      setShippingPartnerPriority([]);
    }
  };

  const fetchB2CConfig = async (sellerId) => {
    if (!canManageB2C) return;
    setB2cConfig(null);
    setB2cPartnersTouched(false);
    try {
      const [cfg, plans] = await Promise.all([
        axios.get(`${API}/b2c/admin/sellers/${sellerId}/config`),
        axios.get(`${API}/b2c/admin/plans`),
      ]);
      const activeIds = (cfg.data.available_partners || []).filter((p) => p.enabled).map((p) => p.id);
      const savedIds = cfg.data.b2c_partner_ids || [];
      const sel = savedIds.length === 0 ? activeIds : savedIds.filter((id) => activeIds.includes(id));
      setB2cPartnerSel(sel);
      // Open the partner list straight away if none of the saved partners is active any more.
      setB2cPartnersOpen(savedIds.length > 0 && sel.length === 0 ? "partners" : "");
      setB2cConfig(cfg.data);
      setB2cPlans((plans.data || []).filter((p) => p.status === "active"));
    } catch (error) {
      console.error("Failed to fetch B2C config:", error);
      toast.error(error.response?.data?.detail || "Failed to load B2C config");
    }
  };

  const toggleB2cPartner = (partnerId, checked) => {
    setB2cPartnerSel((prev) =>
      checked ? [...prev.filter((id) => id !== partnerId), partnerId] : prev.filter((id) => id !== partnerId)
    );
    setB2cPartnersTouched(true);
    setDirty(true);
  };

  const toggleAllB2cPartners = (checked) => {
    setB2cPartnerSel(checked ? b2cActivePartners.map((p) => p.id) : []);
    setB2cPartnersTouched(true);
    setDirty(true);
  };

  // Value saved as b2c_partner_ids. Every active partner ticked => [] ("all", which
  // also picks up partners enabled later). Otherwise the ticked ids, plus any saved
  // ids for partners that are currently disabled (kept, not silently dropped).
  const b2cPartnerIdsPayload = () => {
    if (b2cAllActiveSelected) return [];
    const ticked = b2cActivePartners.filter((p) => b2cPartnerSel.includes(p.id)).map((p) => p.id);
    const keptInactive = b2cInactivePartners.filter((p) => b2cSavedPartnerIds.includes(p.id)).map((p) => p.id);
    return [...ticked, ...keptInactive];
  };

  const addShippingPartnerToPriority = (partnerId) => {
    if (!partnerId || shippingPartnerPriority.includes(partnerId)) return;
    setShippingPartnerPriority([...shippingPartnerPriority, partnerId]);
    setDirty(true);
  };

  const removePartnerFromPriority = (partnerId) => {
    setShippingPartnerPriority(shippingPartnerPriority.filter(id => id !== partnerId));
    setDirty(true);
  };

  const movePartnerUp = (index) => {
    if (index === 0) return;
    const newPriority = [...shippingPartnerPriority];
    [newPriority[index - 1], newPriority[index]] = [newPriority[index], newPriority[index - 1]];
    setShippingPartnerPriority(newPriority);
    setDirty(true);
  };

  const movePartnerDown = (index) => {
    if (index === shippingPartnerPriority.length - 1) return;
    const newPriority = [...shippingPartnerPriority];
    [newPriority[index], newPriority[index + 1]] = [newPriority[index + 1], newPriority[index]];
    setShippingPartnerPriority(newPriority);
    setDirty(true);
  };

  const getPartnerName = (partnerId) => {
    const partner = shippingPartners.find(p => p.id === partnerId);
    return partner ? partner.name : partnerId;
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    fetchSellers(query);
  };

  // Client-side Status + LRN Assignment filters, layered on the server-side search results
  const filteredSellers = useMemo(() => {
    return sellers.filter((s) => {
      if (statusFilter === "active" && !s.is_active) return false;
      if (statusFilter === "inactive" && s.is_active) return false;
      if (lrnFilter === "auto" && s.shipping_assignment_rule !== "auto") return false;
      if (lrnFilter === "manual" && s.shipping_assignment_rule === "auto") return false;
      return true;
    });
  }, [sellers, statusFilter, lrnFilter]);

  // Reset to page 1 whenever a filter or the search changes
  useEffect(() => { setPage(1); }, [statusFilter, lrnFilter, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredSellers.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedSellers = filteredSellers.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const pageAllSelected = pagedSellers.length > 0 && pagedSellers.every((s) => selectedSellerIds.has(s.seller_id));

  const toggleSellerSelect = (sellerId) => {
    setSelectedSellerIds((prev) => {
      const next = new Set(prev);
      if (next.has(sellerId)) next.delete(sellerId);
      else next.add(sellerId);
      return next;
    });
  };

  const toggleSelectAllPage = () => {
    setSelectedSellerIds((prev) => {
      const next = new Set(prev);
      if (pageAllSelected) pagedSellers.forEach((s) => next.delete(s.seller_id));
      else pagedSellers.forEach((s) => next.add(s.seller_id));
      return next;
    });
  };

  const handleBulkLrnAssignment = async () => {
    if (selectedSellerIds.size === 0) return;
    if (bulkAssignmentRule === "auto" && bulkPartnerPriority.length === 0) {
      toast.error("Add at least one shipping partner for auto assignment.");
      return;
    }
    setApplyingBulk(true);
    try {
      const payload = {
        seller_ids: [...selectedSellerIds],
        shipping_assignment_rule: bulkAssignmentRule,
      };
      // Apply the chosen partner priority to every selected seller (auto only).
      if (bulkAssignmentRule === "auto") {
        payload.shipping_partner_priority = bulkPartnerPriority;
      }
      const res = await axios.post(`${API}/sellers-org/bulk-shipping-assignment`, payload);
      toast.success(`LRN assignment updated for ${res.data?.updated ?? selectedSellerIds.size} seller(s)`);
      setShowBulkLrnDialog(false);
      setSelectedSellerIds(new Set());
      fetchSellers(searchQuery);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update LRN assignment");
    } finally {
      setApplyingBulk(false);
    }
  };

  const getCounts = () => {
    const total = sellers.length;
    const active = sellers.filter(s => s.is_active).length;
    const inactive = sellers.filter(s => !s.is_active).length;
    return { total, active, inactive };
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size should be less than 5MB');
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const lookupPincode = async (pincode) => {
    if (pincode.length !== 6) return;

    setPincodeLoading(true);
    try {
      const response = await axios.get(`${API}/pickup-locations/pincode/${pincode}`);
      setFormData((prev) => ({
        ...prev,
        pincode: pincode,
        city: response.data.city,
        state: response.data.state,
      }));
      toast.success("Pincode verified!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid pincode");
    } finally {
      setPincodeLoading(false);
    }
  };

  const handleCreateSeller = async (e) => {
    e.preventDefault();
    
    // Validate 10-digit mobile number
    if (!formData.mobile_number || formData.mobile_number.length !== 10) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }
    
    // Validate 6-digit pincode
    if (!formData.pincode || formData.pincode.length !== 6) {
      toast.error("Please enter a valid 6-digit pincode");
      return;
    }
    
    setSubmitting(true);
    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key]);
      });
      if (logoFile) {
        formDataToSend.append('brand_logo', logoFile);
      }
      
      await axios.post(`${API}/sellers-org/`, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Seller account created successfully!");
      setShowCreateDialog(false);
      resetForm();
      fetchSellers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create seller");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateSeller = async (e) => {
    e.preventDefault();
    
    // Validate 10-digit mobile number
    if (!formData.mobile_number || formData.mobile_number.length !== 10) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }
    
    // Validate 6-digit pincode
    if (!formData.pincode || formData.pincode.length !== 6) {
      toast.error("Please enter a valid 6-digit pincode");
      return;
    }
    
    // Validate shipping assignment if auto is selected
    if (shippingAssignmentRule === "auto" && shippingPartnerPriority.length === 0) {
      toast.error("Please add at least one shipping partner for auto assignment");
      return;
    }

    // B2C partners are only sent when the admin changed them, so an unrelated edit
    // never rewrites the seller's partner list. Validate before any request goes out.
    const sendB2cPartners = canManageB2C && b2cConfig && b2cPartnersTouched && b2cActivePartners.length > 0;
    if (sendB2cPartners && b2cPartnerSel.length === 0) {
      setB2cPartnersOpen("partners");
      toast.error("Select at least one B2C shipping partner, or tick All active partners");
      return;
    }

    setSubmitting(true);
    try {
      // Update seller details
      const updateData = {
        company_registered_name: formData.company_registered_name,
        brand_name: formData.brand_name,
        mobile_number: formData.mobile_number,
        gst_number: formData.gst_number,
        registered_address: formData.registered_address,
        pincode: formData.pincode,
        city: formData.city,
        state: formData.state,
        min_wallet_balance: parseFloat(formData.min_wallet_balance) || 0,
      };
      await axios.put(`${API}/sellers-org/${selectedSeller.seller_id}`, updateData);
      
      // Also save shipping assignment configuration
      await axios.put(`${API}/sellers-org/${selectedSeller.seller_id}/shipping-assignment`, {
        shipping_assignment_rule: shippingAssignmentRule,
        shipping_partner_priority: shippingPartnerPriority
      });

      // Also save the B2C config — folded into the single "Update Seller" action.
      if (canManageB2C && b2cConfig) {
        await axios.put(`${API}/b2c/admin/sellers/${selectedSeller.seller_id}/config`, {
          b2c_enabled: b2cConfig.b2c_enabled,
          b2c_plan_id: b2cConfig.b2c_plan_id || null,
          b2c_cod_cycle_days:
            b2cConfig.b2c_cod_cycle_days === "" || b2cConfig.b2c_cod_cycle_days == null
              ? null
              : Number(b2cConfig.b2c_cod_cycle_days),
          b2c_billing_mode: b2cConfig.b2c_billing_mode || "prepaid",
          // [] = every active partner (see b2cPartnerIdsPayload).
          ...(sendB2cPartners ? { b2c_partner_ids: b2cPartnerIdsPayload() } : {}),
        });
      }

      setDirty(false);
      toast.success("Seller updated successfully!");
      setShowEditDialog(false);
      setSelectedSeller(null);
      resetForm();
      fetchSellers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update seller");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (sellerId, currentStatus) => {
    const action = currentStatus ? "disable" : "enable";
    if (!window.confirm(`Are you sure you want to ${action} this seller? This will also ${action} all associated users.`)) return;

    try {
      const response = await axios.patch(`${API}/sellers-org/${sellerId}/toggle-status`);
      toast.success(response.data.message);
      fetchSellers();
    } catch (error) {
      toast.error("Failed to update seller status");
    }
  };

  const resetForm = () => {
    setFormData({
      company_registered_name: "",
      brand_name: "",
      mobile_number: "",
      gst_number: "",
      registered_address: "",
      pincode: "",
      city: "",
      state: "",
      admin_email: "",
      admin_password: "",
      admin_name: "",
      admin_phone: "",
      min_wallet_balance: "0",
      enforce_wallet_minimum_for_orders: false,
    });
    setLogoFile(null);
    setLogoPreview(null);
  };

  const openEditDialog = (seller) => {
    setSelectedSeller(seller);
    setFormData({
      company_registered_name: seller.company_registered_name,
      brand_name: seller.brand_name,
      mobile_number: seller.mobile_number,
      gst_number: seller.gst_number,
      registered_address: seller.registered_address,
      pincode: seller.pincode,
      city: seller.city,
      state: seller.state,
      admin_email: "",
      admin_password: "",
      admin_name: "",
      admin_phone: "",
      min_wallet_balance: seller.min_wallet_balance?.toString() || "0",
      enforce_wallet_minimum_for_orders: seller.enforce_wallet_minimum_for_orders || false,
    });
    setLogoPreview(seller.brand_logo_url || null);
    setEditSeller(seller);
    // Fetch shipping assignment config and exemption history
    fetchShippingAssignment(seller.seller_id);
    fetchExemptionHistory(seller.seller_id);
    fetchB2CConfig(seller.seller_id);
    setDirty(false);
    setShowEditDialog(true);
  };

  const counts = getCounts();

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-slate-800 mb-2">Seller Accounts</h1>
            <p className="text-slate-600">Manage seller organizations and their admin users</p>
          </div>
          <div className="flex gap-4 items-center flex-wrap">
            <SearchInput
              onSearch={handleSearch}
              placeholder="Search sellers..."
            />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Select value={lrnFilter} onValueChange={setLrnFilter}>
              <SelectTrigger className="w-[170px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All LRN Assignment</SelectItem>
                <SelectItem value="auto">Auto</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" className="gap-1" onClick={() => setShowSettingsDialog(true)}>
              <Settings className="w-4 h-4" /> Wallet Restriction Approvers
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={() => {
                // Pre-seed the priority with the active partners (usually just Delhivery)
                // so the common case is one click; the admin can still reorder/remove.
                setBulkPartnerPriority(shippingPartners.filter((p) => p.is_active).map((p) => p.id));
                setShowBulkLrnDialog(true);
              }}
              disabled={selectedSellerIds.size === 0}
            >
              <Layers className="w-4 h-4" /> Bulk LRN Assignment{selectedSellerIds.size > 0 ? ` (${selectedSellerIds.size})` : ""}
            </Button>
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button data-testid="create-seller-button" style={{backgroundColor: '#00A699'}} onClick={() => resetForm()}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Seller Account
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create New Seller Account</DialogTitle>
                  <DialogDescription>Create seller organization with admin user credentials</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateSeller} className="space-y-4">
                  {/* Logo Upload */}
                  <div className="border-b pb-4">
                    <h4 className="font-semibold mb-3">Brand Logo</h4>
                    <div className="space-y-2">
                      <Label htmlFor="brand_logo">Upload Brand Logo (Optional)</Label>
                      <Input
                        id="brand_logo"
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                      />
                      {logoPreview && (
                        <div className="mt-2">
                          <img src={logoPreview} alt="Logo preview" className="h-20 w-auto object-contain border rounded p-2" />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Company Details */}
                  <div className="border-b pb-4">
                    <h4 className="font-semibold mb-3">Company Information</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="company_registered_name">Company Registered Name *</Label>
                        <Input
                          id="company_registered_name"
                          value={formData.company_registered_name}
                          onChange={(e) => setFormData({...formData, company_registered_name: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="brand_name">Brand Name *</Label>
                        <Input
                          id="brand_name"
                          value={formData.brand_name}
                          onChange={(e) => setFormData({...formData, brand_name: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="mobile_number">Mobile Number *</Label>
                        <Input
                          id="mobile_number"
                          value={formData.mobile_number}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                            setFormData({...formData, mobile_number: value});
                          }}
                          maxLength={10}
                          pattern="[0-9]{10}"
                          placeholder="10 digit mobile number"
                          required
                        />
                        {formData.mobile_number && formData.mobile_number.length !== 10 && (
                          <p className="text-xs text-red-500">Please enter a valid 10-digit mobile number</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="gst_number">GST Number *</Label>
                        <Input
                          id="gst_number"
                          value={formData.gst_number}
                          onChange={(e) => setFormData({...formData, gst_number: e.target.value})}
                          required
                        />
                      </div>
                      <div className="col-span-2 space-y-2">
                        <Label htmlFor="registered_address">Registered Address *</Label>
                        <Input
                          id="registered_address"
                          value={formData.registered_address}
                          onChange={(e) => setFormData({...formData, registered_address: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="pincode">Pincode *</Label>
                        <div className="flex gap-2">
                          <Input
                            id="pincode"
                            value={formData.pincode}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                              setFormData({...formData, pincode: value});
                              if (value.length === 6) lookupPincode(value);
                            }}
                            maxLength={6}
                            pattern="[0-9]{6}"
                            placeholder="6 digit pincode"
                            required
                          />
                          {pincodeLoading && <Loader2 className="w-5 h-5 animate-spin" />}
                        </div>
                        {formData.pincode && formData.pincode.length !== 6 && (
                          <p className="text-xs text-red-500">Please enter a valid 6-digit pincode</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="city">City *</Label>
                        <Input
                          id="city"
                          value={formData.city}
                          onChange={(e) => setFormData({...formData, city: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="state">State *</Label>
                        <Input
                          id="state"
                          value={formData.state}
                          onChange={(e) => setFormData({...formData, state: e.target.value})}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Admin User Details */}
                  <div className="border-b pb-4">
                    <h4 className="font-semibold mb-3">Admin User Credentials</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="admin_name">Admin Name *</Label>
                        <Input
                          id="admin_name"
                          value={formData.admin_name}
                          onChange={(e) => setFormData({...formData, admin_name: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="admin_phone">Admin Phone *</Label>
                        <Input
                          id="admin_phone"
                          value={formData.admin_phone}
                          onChange={(e) => setFormData({...formData, admin_phone: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="admin_email">Admin Email *</Label>
                        <Input
                          id="admin_email"
                          type="email"
                          value={formData.admin_email}
                          onChange={(e) => setFormData({...formData, admin_email: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="admin_password">Admin Password *</Label>
                        <div className="relative">
                          <Input
                            id="admin_password"
                            type={showPassword ? "text" : "password"}
                            value={formData.admin_password}
                            onChange={(e) => setFormData({...formData, admin_password: e.target.value})}
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-2 top-1/2 -translate-y-1/2"
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" style={{backgroundColor: '#00A699'}} disabled={submitting}>
                      {submitting ? "Creating..." : "Create Seller Account"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-0 shadow-lg">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Total Sellers</p>
                  <p className="text-3xl font-bold text-slate-800">{counts.total}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Active Sellers</p>
                  <p className="text-3xl font-bold text-green-600">{counts.active}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <ShieldCheck className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Inactive Sellers</p>
                  <p className="text-3xl font-bold text-red-600">{counts.inactive}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <ShieldX className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sellers Table */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
          </div>
        ) : filteredSellers.length === 0 ? (
          <Card className="border-0 shadow-lg">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Users className="w-16 h-16 text-slate-300 mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No sellers found</h3>
              <p className="text-slate-500">
                {sellers.length === 0
                  ? "Create your first seller account to get started"
                  : "No sellers match the current filters"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap w-10">
                      <Checkbox
                        checked={pageAllSelected}
                        onCheckedChange={toggleSelectAllPage}
                        aria-label="Select all sellers on this page"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Logo</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Seller ID</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Brand Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Company Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Contact</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Location</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Wallet Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">LRN Assignment</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Added On</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {pagedSellers.map((seller) => (
                    <tr key={seller.seller_id} className={!seller.commercial_structures?.length ? "bg-red-50 hover:bg-red-100" : "hover:bg-slate-50"}>
                      <td className="px-4 py-4 align-middle whitespace-nowrap">
                        <Checkbox
                          checked={selectedSellerIds.has(seller.seller_id)}
                          onCheckedChange={() => toggleSellerSelect(seller.seller_id)}
                          aria-label={`Select ${seller.brand_name}`}
                        />
                      </td>
                      <td className="px-4 py-4 align-middle whitespace-nowrap">
                        {seller.brand_logo_url ? (
                          <img 
                            src={seller.brand_logo_url} 
                            alt={seller.brand_name}
                            className="h-10 w-10 object-contain rounded border"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded border bg-slate-100 flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-slate-400" />
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 align-middle whitespace-nowrap">
                        <span className="text-sm font-medium text-slate-900">{seller.seller_id}</span>
                      </td>
                      <td className="px-4 py-4 align-middle whitespace-nowrap">
                        <span className="text-sm font-medium text-slate-800">{seller.brand_name}</span>
                      </td>
                      <td className="px-4 py-4 align-middle">
                        <span className="text-sm text-slate-600">{seller.company_registered_name}</span>
                      </td>
                      <td className="px-4 py-4 align-middle">
                        <div className="text-sm text-slate-600">
                          <div>{seller.mobile_number}</div>
                        </div>
                      </td>
                      <td className="px-4 py-4 align-middle">
                        <div className="text-sm text-slate-600">
                          <div>{seller.city}, {seller.state}</div>
                        </div>
                      </td>
                      <td className="px-4 py-4 align-middle whitespace-nowrap text-right">
                        <span className={`text-sm font-medium ${seller.wallet_balance < 0 ? 'text-red-600' : 'text-slate-900'}`}>
                          ₹{(seller.wallet_balance || 0).toLocaleString()}
                        </span>
                      </td>
                      <td className="px-4 py-4 align-middle whitespace-nowrap">
                        {seller.shipping_assignment_rule === "auto" ? (
                          <Badge className="bg-blue-100 text-blue-800 border-0">
                            <Truck className="w-3 h-3 mr-1" />
                            Auto
                          </Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-600 border-0">
                            Manual
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-4 align-middle whitespace-nowrap">
                        <div className="text-sm text-slate-600">
                          <div>{formatToIST(seller.created_at).split(', ')[0]}</div>
                          <div className="text-xs text-slate-500">{formatToIST(seller.created_at).split(', ')[1]}</div>
                        </div>
                      </td>
                      <td className="px-4 py-4 align-middle whitespace-nowrap">
                        {seller.is_active ? (
                          <Badge className="bg-green-100 text-green-800 border-0">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Active
                          </Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-800 border-0">
                            <XCircle className="w-3 h-3 mr-1" />
                            Inactive
                          </Badge>
                        )}
                        {!seller.commercial_structures?.length && (
                          <Badge className="bg-red-100 text-red-800 border-0 mt-1 flex items-center w-fit" title="No commercial/billing structure configured — orders can't be created until it's set">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Billing Not Set
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-4 align-middle whitespace-nowrap text-sm space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditDialog(seller)}
                        >
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleToggleStatus(seller.seller_id, seller.is_active)}
                          className={seller.is_active ? "border-red-200 text-red-600" : "border-green-200 text-green-600"}
                        >
                          {seller.is_active ? <XCircle className="w-3 h-3" /> : <CheckCircle className="w-3 h-3" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/admin/sellers/${seller.seller_id}/wallet`)}
                        >
                          Wallet
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/admin/sellers/${seller.seller_id}/commercial-structure`)}
                          style={{borderColor: '#00A699', color: '#00A699'}}
                        >
                          Billing
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setRateCardSeller(seller)}
                          title="B2B rate card (upload agreement, review & activate)"
                        >
                          <ScrollText className="w-3 h-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 border-t border-slate-200">
              <Pagination
                page={currentPage}
                pages={totalPages}
                total={filteredSellers.length}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
              />
            </div>
          </div>
        )}

        {/* Per-seller B2B rate-card manager (upload agreement → extract → confirm) */}
        <SellerRateCardManager
          seller={rateCardSeller}
          open={!!rateCardSeller}
          onOpenChange={(o) => { if (!o) setRateCardSeller(null); }}
        />

        {/* Edit Dialog */}
        <Dialog
          open={showEditDialog}
          onOpenChange={(open) => {
            if (open) { setShowEditDialog(true); return; }
            // Guard accidental close (overlay / ESC / X) when there are unsaved edits.
            if (dirty && !window.confirm("You have unsaved changes on this seller. Discard them?")) return;
            setDirty(false);
            setShowEditDialog(false);
          }}
        >
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Seller Account</DialogTitle>
              <DialogDescription>Update seller organization details</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateSeller} onInput={() => setDirty(true)} className="space-y-4">
              {/* Show current logo */}
              {(logoPreview || selectedSeller?.brand_logo_url) && (
                <div className="border-b pb-4">
                  <h4 className="font-semibold mb-3">Current Brand Logo</h4>
                  <img 
                    src={logoPreview || selectedSeller?.brand_logo_url} 
                    alt="Brand logo" 
                    className="h-20 w-auto object-contain border rounded p-2"
                  />
                  <p className="text-xs text-slate-500 mt-2">Logo cannot be updated in edit mode. Please contact support to change logo.</p>
                </div>
              )}

              <div className="border-b pb-4">
                <h4 className="font-semibold mb-3">Company Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit_company_registered_name">Company Registered Name *</Label>
                    <Input
                      id="edit_company_registered_name"
                      value={formData.company_registered_name}
                      onChange={(e) => setFormData({...formData, company_registered_name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit_brand_name">Brand Name *</Label>
                    <Input
                      id="edit_brand_name"
                      value={formData.brand_name}
                      onChange={(e) => setFormData({...formData, brand_name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit_mobile_number">Mobile Number *</Label>
                    <Input
                      id="edit_mobile_number"
                      value={formData.mobile_number}
                      onChange={(e) => setFormData({...formData, mobile_number: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit_gst_number">GST Number *</Label>
                    <Input
                      id="edit_gst_number"
                      value={formData.gst_number}
                      onChange={(e) => setFormData({...formData, gst_number: e.target.value})}
                      required
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="edit_registered_address">Registered Address *</Label>
                    <Input
                      id="edit_registered_address"
                      value={formData.registered_address}
                      onChange={(e) => setFormData({...formData, registered_address: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit_pincode">Pincode *</Label>
                    <div className="flex gap-2">
                      <Input
                        id="edit_pincode"
                        value={formData.pincode}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormData({...formData, pincode: value});
                          if (value.length === 6) lookupPincode(value);
                        }}
                        maxLength={6}
                        required
                      />
                      {pincodeLoading && <Loader2 className="w-5 h-5 animate-spin" />}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit_city">City *</Label>
                    <Input
                      id="edit_city"
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit_state">State *</Label>
                    <Input
                      id="edit_state"
                      value={formData.state}
                      onChange={(e) => setFormData({...formData, state: e.target.value})}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="border-b pb-4">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Truck className="w-4 h-4" />
                  Shipping Assignment Rule
                </h4>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Assignment Type</Label>
                    <Select value={shippingAssignmentRule} onValueChange={(v) => { setShippingAssignmentRule(v); setDirty(true); }}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select assignment type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manual">Manual - LRN assigned manually after order creation</SelectItem>
                        <SelectItem value="auto">Auto - LRN assigned automatically via shipping partner API</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                      {shippingAssignmentRule === "manual" 
                        ? "LRN will be assigned manually after order creation"
                        : "System will automatically call shipping partner API to get LRN when order is created"}
                    </p>
                  </div>
                  
                  {shippingAssignmentRule === "auto" && (
                    <div className="space-y-3">
                      <Label>Shipping Partner Priority (order of preference)</Label>
                      <p className="text-xs text-slate-500">
                        When auto assignment is enabled, system will try partners in this order. If one fails, it will try the next.
                      </p>
                      
                      {/* Priority List */}
                      {shippingPartnerPriority.length > 0 && (
                        <div className="space-y-2 bg-slate-50 p-3 rounded-lg">
                          {shippingPartnerPriority.map((partnerId, index) => (
                            <div key={partnerId} className="flex items-center gap-2 bg-white p-2 rounded border">
                              <span className="text-sm font-semibold text-slate-600 w-6">{index + 1}.</span>
                              <GripVertical className="w-4 h-4 text-slate-400" />
                              <span className="flex-1 text-sm font-medium">{getPartnerName(partnerId)}</span>
                              <div className="flex items-center gap-1">
                                <Button 
                                  type="button" 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => movePartnerUp(index)}
                                  disabled={index === 0}
                                  className="h-7 w-7 p-0"
                                >
                                  <ArrowUp className="w-3 h-3" />
                                </Button>
                                <Button 
                                  type="button" 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => movePartnerDown(index)}
                                  disabled={index === shippingPartnerPriority.length - 1}
                                  className="h-7 w-7 p-0"
                                >
                                  <ArrowDown className="w-3 h-3" />
                                </Button>
                                <Button 
                                  type="button" 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => removePartnerFromPriority(partnerId)}
                                  className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Add Partner Dropdown */}
                      {shippingPartners.filter(p => !shippingPartnerPriority.includes(p.id)).length > 0 && (
                        <div className="flex gap-2">
                          <Select onValueChange={addShippingPartnerToPriority}>
                            <SelectTrigger className="flex-1">
                              <SelectValue placeholder="Add shipping partner to priority list" />
                            </SelectTrigger>
                            <SelectContent>
                              {shippingPartners
                                .filter(p => !shippingPartnerPriority.includes(p.id))
                                .map(partner => (
                                  <SelectItem key={partner.id} value={partner.id}>
                                    {partner.name} {!partner.is_active && "(Inactive)"}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      
                      {shippingPartnerPriority.length === 0 && (
                        <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded">
                          ⚠️ Please add at least one shipping partner to enable auto assignment
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {canManageB2C && (
                <div className="border-b pb-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <PackageCheck className="w-4 h-4 text-brand" /> B2C Shipping
                  </h4>
                  {!b2cConfig ? (
                    <p className="text-sm text-slate-400">Loading B2C config…</p>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                        <div>
                          <Label className="font-medium">Enable B2C last-mile</Label>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {b2cConfig.b2c_enabled
                              ? "Seller can create B2C orders (a plan must be assigned)"
                              : "B2C section hidden for this seller"}
                          </p>
                        </div>
                        <Switch
                          checked={!!b2cConfig.b2c_enabled}
                          onCheckedChange={(v) => { setB2cConfig({ ...b2cConfig, b2c_enabled: v }); setDirty(true); }}
                          data-testid="b2c-enable-switch"
                        />
                      </div>
                      <Accordion
                        type="single"
                        collapsible
                        value={b2cPartnersOpen}
                        onValueChange={setB2cPartnersOpen}
                      >
                        <AccordionItem value="partners" className="rounded-lg border bg-white">
                          <AccordionTrigger className="px-3 py-3 hover:no-underline" data-testid="b2c-partners-trigger">
                            <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1 pr-2">
                              <Truck className="h-4 w-4 shrink-0 text-slate-500" />
                              <span className="font-medium text-slate-800">Shipping partners</span>
                              <span
                                className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${b2cPartnerSummary.className}`}
                                data-testid="b2c-partners-summary"
                              >
                                {b2cPartnerSummary.label}
                              </span>
                            </span>
                          </AccordionTrigger>
                          <AccordionContent className="space-y-3 px-3">
                            {b2cPartners.length === 0 ? (
                              <p className="text-sm text-slate-500">
                                No B2C partners integrated yet. They appear here once added on the B2C Shipping Partners page.
                              </p>
                            ) : (
                              <>
                                {b2cActivePartners.length === 0 ? (
                                  <p className="text-sm text-slate-500">
                                    No partner is active. Enable one on the B2C Shipping Partners page to make it available to sellers.
                                  </p>
                                ) : (
                                  <>
                                    <p className="text-xs text-slate-500">
                                      {b2cAllActiveSelected
                                        ? "This seller quotes and ships through every active partner. Partners enabled later are included automatically."
                                        : "This seller quotes and ships only through the ticked partners. Tick All active partners to include every active partner again."}
                                    </p>
                                    <label className="flex min-h-11 cursor-pointer items-center gap-3 rounded-md border bg-slate-50 px-3 py-2">
                                      <Checkbox
                                        checked={b2cAllActiveSelected}
                                        onCheckedChange={(v) => toggleAllB2cPartners(v === true)}
                                        data-testid="b2c-partners-all"
                                      />
                                      <span className="flex-1 text-sm font-medium text-slate-800">All active partners</span>
                                      <span className="text-xs tabular-nums text-slate-500">
                                        {b2cPartnerSel.length}/{b2cActivePartners.length} selected
                                      </span>
                                    </label>
                                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="b2c-partner-list">
                                      {b2cActivePartners.map((p) => (
                                        <label
                                          key={p.id}
                                          className="flex min-h-11 cursor-pointer items-center gap-3 rounded-md border bg-white px-3 py-2"
                                        >
                                          <Checkbox
                                            checked={b2cPartnerSel.includes(p.id)}
                                            onCheckedChange={(v) => toggleB2cPartner(p.id, v === true)}
                                            data-testid={`b2c-partner-${p.name}`}
                                          />
                                          <span className="min-w-0 flex-1 truncate text-sm text-slate-800">
                                            {p.display_name || p.name}
                                          </span>
                                        </label>
                                      ))}
                                    </div>
                                    {b2cPartnerSel.length === 0 && (
                                      <p
                                        role="alert"
                                        className={`flex items-start gap-1.5 rounded-md p-2 text-xs ${
                                          b2cPartnersTouched ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-800"
                                        }`}
                                      >
                                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                        {b2cPartnersTouched
                                          ? "Select at least one partner, or tick All active partners."
                                          : "None of this seller's selected partners is active right now, so B2C shipping will fail. Select an active partner."}
                                      </p>
                                    )}
                                  </>
                                )}
                                {b2cInactivePartners.length > 0 && (
                                  <div className="space-y-1.5 border-t pt-3">
                                    <p className="text-xs font-medium text-slate-600">
                                      Disabled on the B2C Shipping Partners page
                                    </p>
                                    <ul className="space-y-1.5">
                                      {b2cInactivePartners.map((p) => (
                                        <li
                                          key={p.id}
                                          className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-dashed px-3 py-2 text-sm text-slate-500"
                                          data-testid={`b2c-partner-disabled-${p.name}`}
                                        >
                                          <span className="min-w-0 flex-1 truncate">{p.display_name || p.name}</span>
                                          {!b2cAllActiveSelected && b2cSavedPartnerIds.includes(p.id) && (
                                            <span className="text-xs">Saved for this seller</span>
                                          )}
                                          <Badge className="border-0 bg-slate-100 text-slate-600">Disabled</Badge>
                                        </li>
                                      ))}
                                    </ul>
                                    <p className="text-xs text-slate-500">
                                      These can't be selected until they're enabled there.
                                      {b2cAllActiveSelected && " Sellers on All active partners pick them up automatically once enabled."}
                                    </p>
                                  </div>
                                )}
                              </>
                            )}
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                      <div className="space-y-2">
                        <Label htmlFor="b2c-plan-select">Pricing Plan</Label>
                        <Select
                          value={b2cConfig.b2c_plan_id || "__default__"}
                          onValueChange={(v) => {
                            setB2cConfig({ ...b2cConfig, b2c_plan_id: v === "__default__" ? null : v });
                            setDirty(true);
                          }}
                        >
                          <SelectTrigger id="b2c-plan-select" data-testid="b2c-plan-select">
                            <SelectValue placeholder="Default plan" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__default__">Default plan</SelectItem>
                            {b2cPlans.map((p) => (
                              <SelectItem key={p.id} value={p.id}>
                                {p.name}{p.is_default ? " (default)" : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-slate-500">
                          Drives the seller's rate card. Leave on "Default plan" to follow the platform default.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="b2c-cod-cycle">COD payout cycle override (days)</Label>
                        <Input
                          id="b2c-cod-cycle"
                          type="number"
                          min="1"
                          placeholder={`Plan default (${b2cConfig.effective_cod_cycle_days ?? 8} days)`}
                          value={b2cConfig.b2c_cod_cycle_days ?? ""}
                          onChange={(e) =>
                            setB2cConfig({ ...b2cConfig, b2c_cod_cycle_days: e.target.value })
                          }
                          data-testid="b2c-cod-cycle-input"
                        />
                        <p className="text-xs text-slate-500">
                          Blank = use the plan's cycle. Effective: {b2cConfig.effective_cod_cycle_days ?? 8} days.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="b2c-billing-mode">Billing mode</Label>
                        <Select
                          value={b2cConfig.b2c_billing_mode || "prepaid"}
                          onValueChange={(v) => { setB2cConfig({ ...b2cConfig, b2c_billing_mode: v }); setDirty(true); }}
                        >
                          <SelectTrigger id="b2c-billing-mode" data-testid="b2c-billing-mode-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="prepaid">Prepaid</SelectItem>
                            <SelectItem value="postpaid">Postpaid</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-slate-500">
                          {b2cConfig.b2c_billing_mode === "postpaid"
                            ? "Postpaid — wallet may go negative down to the Minimum Wallet Balance Threshold (set it negative to act as a credit limit). The bill is set off on the next recharge."
                            : "Prepaid — wallet must stay at or above the Minimum Wallet Balance Threshold to ship."}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="border-b pb-4">
                <h4 className="font-semibold mb-3">Wallet & Order Settings</h4>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit_min_wallet_balance">Minimum Wallet Balance Threshold (₹)</Label>
                    <Input
                      id="edit_min_wallet_balance"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.min_wallet_balance}
                      onChange={(e) => setFormData({...formData, min_wallet_balance: e.target.value})}
                      placeholder="0.00"
                    />
                    <p className="text-xs text-slate-500">Low balance alert will be sent when wallet balance falls below this amount</p>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                    <div>
                      <Label className="font-medium">Restrict Order Creation</Label>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {formData.enforce_wallet_minimum_for_orders
                          ? "Enabled — orders blocked when wallet is below minimum threshold"
                          : "Disabled — orders allowed regardless of wallet balance"}
                      </p>
                      {!formData.enforce_wallet_minimum_for_orders && editSeller?.wallet_restriction_override?.status === "active" && (
                        <p className="text-xs text-amber-600 mt-1">
                          Exemption active until {editSeller.wallet_restriction_override.exempt_until?.slice(0, 10)}
                        </p>
                      )}
                    </div>
                    {formData.enforce_wallet_minimum_for_orders ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                        onClick={() => {
                          setExemptionSeller(editSeller);
                          setShowExemptionDialog(true);
                        }}
                      >
                        Request Exemption
                      </Button>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-700 border-0">Exempted</Badge>
                    )}
                  </div>

                  {/* Exemption History */}
                  <div className="mt-3">
                    <Label className="text-xs font-semibold text-slate-600">Exemption Request History</Label>
                    {historyLoading ? (
                      <div className="flex items-center gap-2 py-3 text-xs text-slate-400"><Loader2 className="w-3 h-3 animate-spin" /> Loading...</div>
                    ) : exemptionHistory.length === 0 ? (
                      <p className="text-xs text-slate-400 py-2 italic">No exemption requests yet</p>
                    ) : (
                      <div className="mt-1 max-h-48 overflow-y-auto space-y-1.5">
                        {exemptionHistory.map((req) => {
                          const statusColors = {
                            approval_pending: "bg-amber-100 text-amber-700",
                            approved: "bg-green-100 text-green-700",
                            rejected: "bg-red-100 text-red-700",
                            expired: "bg-slate-100 text-slate-500",
                          };
                          return (
                            <div key={req.id} className="flex items-start justify-between p-2.5 bg-white rounded border text-xs">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <Badge className={`${statusColors[req.status] || "bg-slate-100 text-slate-600"} border-0 text-[10px]`}>
                                    {req.status?.replace("_", " ").toUpperCase()}
                                  </Badge>
                                  <span className="text-slate-500">{req.exemption_days} days</span>
                                </div>
                                <div className="text-slate-500 mt-1">
                                  Requested by <span className="text-slate-700 font-medium">{req.requested_by}</span>
                                  <span className="text-slate-300 mx-1">|</span>
                                  {new Date(req.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" })}
                                </div>
                                {req.approved_by && (
                                  <div className="text-slate-500 mt-0.5">
                                    {req.status === "approved" || req.status === "expired" ? "Approved" : "Decided"} by <span className="text-slate-700 font-medium">{req.approved_by}</span>
                                    {req.exempt_until && <span className="text-slate-400"> | until {req.exempt_until.slice(0, 10)}</span>}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => { setDirty(false); setShowEditDialog(false); }}>
                  Cancel
                </Button>
                <Button type="submit" style={{backgroundColor: '#00A699'}} disabled={submitting}>
                  {submitting ? "Updating..." : "Update Seller"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
        {/* Wallet Restriction Approver Settings Dialog */}
        <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Settings className="w-4 h-4 text-teal-600" /> Wallet Restriction Approver Settings</DialogTitle>
              <DialogDescription>Configure who can approve wallet restriction exemption requests.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {[
                { label: "Approver Emails", field: "approver_emails", value: newApproverEmail, setter: setNewApproverEmail, desc: "Receive approval links via email" },
                { label: "Notification To", field: "notification_to_emails", value: newToEmail, setter: setNewToEmail, desc: "Notified on approval/rejection decisions" },
                { label: "CC", field: "notification_cc_emails", value: newCcEmail, setter: setNewCcEmail },
                { label: "BCC", field: "notification_bcc_emails", value: newBccEmail, setter: setNewBccEmail },
              ].map(({ label, field, value, setter, desc }) => (
                <div key={field}>
                  <Label className="text-xs font-semibold">{label}</Label>
                  {desc && <p className="text-xs text-slate-400 mb-1">{desc}</p>}
                  <div className="flex gap-2 mb-1">
                    <Input className="h-8 text-xs" value={value} onChange={(e) => setter(e.target.value)} placeholder="email@example.com"
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmail(field, value, setter); } }} />
                    <Button type="button" size="sm" className="h-8 text-xs" style={{ backgroundColor: '#00A699' }} onClick={() => addEmail(field, value, setter)}>Add</Button>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {walletSettings[field]?.map((email) => (
                      <Badge key={email} variant="outline" className="text-xs gap-1">
                        {email}
                        <button onClick={() => removeEmail(field, email)} className="text-slate-400 hover:text-red-500 ml-0.5">&times;</button>
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
              <Button className="w-full" style={{ backgroundColor: '#00A699' }} onClick={saveWalletSettings} disabled={savingSettings}>
                {savingSettings ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Save Settings
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Exemption Request Dialog */}
        <Dialog open={showExemptionDialog} onOpenChange={setShowExemptionDialog}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Clock className="w-4 h-4 text-amber-600" /> Request Wallet Restriction Exemption</DialogTitle>
              <DialogDescription>
                Temporarily allow {exemptionSeller?.brand_name || "this seller"} to create orders below wallet minimum. Requires approver sign-off.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <Label className="text-xs font-semibold">Seller</Label>
                <p className="text-sm font-medium text-slate-800">{exemptionSeller?.brand_name} ({exemptionSeller?.seller_id})</p>
              </div>
              <div>
                <Label className="text-xs font-semibold">Exemption Duration (days)</Label>
                <Select value={exemptionDays} onValueChange={setExemptionDays}>
                  <SelectTrigger className="h-9 text-sm mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 5, 7, 10, 14, 30].map((d) => (
                      <SelectItem key={d} value={String(d)}>{d} day{d > 1 ? "s" : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button className="w-full" style={{ backgroundColor: '#00A699' }} onClick={handleRequestExemption} disabled={requestingExemption}>
                {requestingExemption ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Mail className="w-4 h-4 mr-1" />}
                Send for Approval
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Bulk LRN Assignment Dialog */}
        <Dialog open={showBulkLrnDialog} onOpenChange={setShowBulkLrnDialog}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Layers className="w-4 h-4 text-teal-600" /> Bulk LRN Assignment</DialogTitle>
              <DialogDescription>
                Apply an LRN assignment rule to {selectedSellerIds.size} selected seller{selectedSellerIds.size === 1 ? "" : "s"}.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label>Assignment Type</Label>
                <Select value={bulkAssignmentRule} onValueChange={setBulkAssignmentRule}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select assignment type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto - LRN assigned automatically via shipping partner API</SelectItem>
                    <SelectItem value="manual">Manual - LRN assigned manually after order creation</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  {bulkAssignmentRule === "auto"
                    ? "System will automatically call the shipping partner API to get an LRN when an order is created. The partner priority below is applied to every selected seller."
                    : "LRN will be assigned manually after order creation."}
                </p>
              </div>
              {bulkAssignmentRule === "auto" && (
                <PartnerPriorityEditor
                  priority={bulkPartnerPriority}
                  setPriority={setBulkPartnerPriority}
                  partners={shippingPartners}
                />
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowBulkLrnDialog(false)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  style={{ backgroundColor: '#00A699' }}
                  onClick={handleBulkLrnAssignment}
                  disabled={applyingBulk || selectedSellerIds.size === 0}
                >
                  {applyingBulk ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Applying...</>
                  ) : (
                    "Apply"
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

    </AdminLayout>
  );
};

export default SellersManagement;
